import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { MediaLayer } from "../http/foyer";

/**
 * Which media layer the surrounding page is working in.
 *
 * Media has three layers - Global media, a customer's shared library, and one
 * location's own files - and a component like `MediaGallery` or an
 * `AssetField` picker can appear in any of them. Threading the layer through
 * every editor in between is exactly the prop-drilling this context exists to
 * avoid.
 *
 * The value is BOTH ids rather than a single layer, because a location is
 * always *also* inside its customer: the Content view reads its own files plus
 * the inherited company ones, and the picker offers all three as tabs. Outside
 * any of that (the Global media page) both are undefined.
 */
export interface MediaScope {
  /** The location in view, if any (premise slug). */
  premiseId?: string;
  /** The customer that location belongs to - or the one selected on its own. */
  companyId?: string;
}

const PremiseScopeContext = createContext<MediaScope>({});

export function PremiseScopeProvider({
  premiseId,
  companyId,
  children,
}: {
  premiseId?: string;
  companyId?: string;
  children: ReactNode;
}) {
  const value = useMemo<MediaScope>(
    () => ({
      premiseId: premiseId || undefined,
      companyId: companyId || undefined,
    }),
    [premiseId, companyId]
  );
  return (
    <PremiseScopeContext.Provider value={value}>
      {children}
    </PremiseScopeContext.Provider>
  );
}

/** The current media scope. `{}` on the Global media page. */
export function useMediaScope(): MediaScope {
  return useContext(PremiseScopeContext);
}

/** The current premise slug, or `undefined`. Kept for the non-media consumers
 *  (Instagram connect, menu feeds) that only ever wanted the location. */
export function usePremiseScope(): string | undefined {
  return useContext(PremiseScopeContext).premiseId;
}

/**
 * The layer a page WRITES to, given its scope: the location if one is in view,
 * else the customer, else global. Uploads and deletes follow this - never the
 * inherited layer, which is read-only wherever it is merely visible.
 */
export function writeLayer(scope: MediaScope): MediaLayer {
  if (scope.premiseId) return { kind: "premise", premiseId: scope.premiseId };
  if (scope.companyId) return { kind: "company", companyId: scope.companyId };
  return { kind: "global" };
}
